import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Download, Crown, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { billingService, AdminHostBalance, AdminBalanceListParams, AdminBalanceListResponse } from '../../services/billingService';
import { useNotification } from '../../hooks/useNotification';
import { useAppSelector } from '../../store/hooks';
import Button from '../../components/UI/Button';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { BalanceDisplay } from '../../components/Billing';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { logError } from '../../utils/errorLogger';
import { BillingTransaction, TopUpData } from '../../services/billingService';
import AdminBalanceSummary from '../../components/Billing/AdminBalanceSummary';
import AdminBalanceFilters from '../../components/Billing/AdminBalanceFilters';
import AdminBalanceTable from '../../components/Billing/AdminBalanceTable';
import MirsalMemberManagementModal from '../../components/Billing/MirsalMemberManagementModal';
import { DollarSign } from 'lucide-react';

const AdminBalancePage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { theme } = useAppSelector((state) => state.ui);
  
  const [data, setData] = useState<AdminBalanceListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [filters, setFilters] = useState<AdminBalanceListParams>({
    filter_balance_status: 'all',
    filter_mirsal_member: 'all',
    sort_by: 'host_name',
    sort_order: 'asc',
    page: 1,
  });
  
  // Top-up modal state
  const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false);
  const [selectedBalance, setSelectedBalance] = useState<AdminHostBalance | null>(null);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpDescription, setTopUpDescription] = useState('');
  const [topUpErrors, setTopUpErrors] = useState<Record<string, string>>({});

  // Mirsal member management modal state
  const [isMirsalMemberModalOpen, setIsMirsalMemberModalOpen] = useState(false);

  // Load balances
  const loadBalances = useCallback(async () => {
    try {
      setLoading(true);
      const response = await billingService.getAdminHostBalances(filters);
      setData(response);
    } catch (error: any) {
      logError('Failed to load host balances', error);
      showNotification('Failed to load host balances', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showNotification]);

  useEffect(() => {
    loadBalances();
  }, [loadBalances]);

  // Handle top-up
  const handleTopUp = async () => {
    if (!selectedBalance) return;

    // Validate
    const errors: Record<string, string> = {};
    const amount = parseFloat(topUpAmount);
    if (isNaN(amount) || amount <= 0) {
      errors.amount = 'Amount must be greater than 0';
    }
    setTopUpErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      setActionLoading('topup');
      const topUpData: TopUpData = {
        amount: topUpAmount,
        description: topUpDescription || 'Manual top-up'
      };
      const response = await billingService.topUpBalance(selectedBalance.id, topUpData);
      
      if (response.success) {
        showNotification(`Successfully added ${topUpAmount} JOD to balance`, 'success');
        setIsTopUpModalOpen(false);
        setSelectedBalance(null);
        setTopUpAmount('');
        setTopUpDescription('');
        await loadBalances();
      } else {
        showNotification(response.error?.message || 'Failed to top up balance', 'error');
      }
    } catch (error: any) {
      logError('Failed to top up balance', error);
      showNotification(error.response?.data?.error?.message || 'Failed to top up balance', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleFiltersChange = (newFilters: AdminBalanceListParams) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      filter_balance_status: 'all',
      filter_mirsal_member: 'all',
      sort_by: 'host_name',
      sort_order: 'asc',
      page: 1,
    });
  };

  const handleSort = (field: string) => {
    setFilters(prev => ({
      ...prev,
      sort_by: field as any,
      sort_order: prev.sort_by === field && prev.sort_order === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleTopUpClick = (balance: AdminHostBalance) => {
    setSelectedBalance(balance);
    setTopUpAmount('');
    setTopUpDescription('');
    setTopUpErrors({});
    setIsTopUpModalOpen(true);
  };

  if (loading && !data) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl sm:text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Host Balances
          </h1>
          <p className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            View and manage host account balances
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <Button
            variant="primary"
            onClick={() => navigate('/admin/billing/pricing')}
            leftIcon={<Settings className="h-4 w-4" />}
            size="sm"
            className="flex-1 sm:flex-initial"
          >
            <span className="hidden sm:inline">Pricing</span>
            <span className="sm:hidden">Pricing</span>
          </Button>
          <Button
            variant="primary"
            onClick={() => setIsMirsalMemberModalOpen(true)}
            leftIcon={<Crown className="h-4 w-4" />}
            size="sm"
            className="flex-1 sm:flex-initial"
          >
            <span className="hidden sm:inline">Manage Mirsal Members</span>
            <span className="sm:hidden">Mirsal</span>
          </Button>
          <Button
            variant="outline"
            onClick={loadBalances}
            leftIcon={<RefreshCw className="h-4 w-4" />}
            loading={loading}
            size="sm"
            className="flex-1 sm:flex-initial"
          >
            <span className="hidden sm:inline">Refresh</span>
            <span className="sm:hidden">Refresh</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              // Placeholder for CSV export
              showNotification('Export feature coming soon', 'info');
            }}
            leftIcon={<Download className="h-4 w-4" />}
            size="sm"
            className="flex-1 sm:flex-initial"
          >
            <span className="hidden sm:inline">Export</span>
            <span className="sm:hidden">Export</span>
          </Button>
        </div>
      </div>

      {/* Summary Statistics */}
      {data && (
        <AdminBalanceSummary summary={data.summary} loading={loading} />
      )}

      {/* Filters */}
      <AdminBalanceFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
      />

      {/* Table */}
      {data && (
        <AdminBalanceTable
          balances={data.results}
          loading={loading}
          onTopUp={handleTopUpClick}
          sortBy={filters.sort_by}
          sortOrder={filters.sort_order}
          onSort={handleSort}
          onBalanceChange={loadBalances}
        />
      )}

      {/* Pagination */}
      {data && data.count > 0 && (
        <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 rounded-lg border p-4 ${
          theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-sm text-center sm:text-left ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Showing {data.results.length} of {data.count} hosts
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange((filters.page || 1) - 1)}
              disabled={!data.previous || loading}
              className="flex-1 sm:flex-initial"
            >
              Previous
            </Button>
            <span className={`px-2 sm:px-4 py-2 text-sm whitespace-nowrap ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Page {filters.page || 1} of {Math.ceil(data.count / 25)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange((filters.page || 1) + 1)}
              disabled={!data.next || loading}
              className="flex-1 sm:flex-initial"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Top-Up Modal */}
      <Modal
        isOpen={isTopUpModalOpen}
        onClose={() => {
          setIsTopUpModalOpen(false);
          setSelectedBalance(null);
          setTopUpAmount('');
          setTopUpDescription('');
          setTopUpErrors({});
        }}
        title={`Top Up Balance - ${selectedBalance?.host_name}`}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              value={topUpAmount}
              onChange={(e) => {
                setTopUpAmount(e.target.value);
                if (topUpErrors.amount) {
                  setTopUpErrors(prev => ({ ...prev, amount: '' }));
                }
              }}
              error={topUpErrors.amount}
              label="Amount (JOD) *"
              leftIcon={<DollarSign className="h-4 w-4" />}
              required
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Description (Optional)
            </label>
            <textarea
              value={topUpDescription}
              onChange={(e) => setTopUpDescription(e.target.value)}
              rows={3}
              className={`w-full px-4 py-2.5 rounded-md border-2 transition-colors ${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green'
                  : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green'
              }`}
              placeholder="Optional description for this top-up..."
            />
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="outline"
              onClick={() => {
                setIsTopUpModalOpen(false);
                setSelectedBalance(null);
                setTopUpAmount('');
                setTopUpDescription('');
                setTopUpErrors({});
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleTopUp}
              loading={actionLoading === 'topup'}
              leftIcon={<DollarSign className="h-4 w-4" />}
            >
              Add Balance
            </Button>
          </div>
        </div>
      </Modal>

      {/* Mirsal Member Management Modal */}
      <MirsalMemberManagementModal
        isOpen={isMirsalMemberModalOpen}
        onClose={() => setIsMirsalMemberModalOpen(false)}
        onUpdate={() => {
          // Reload balances to reflect any changes
          loadBalances();
        }}
      />
    </div>
  );
};

export default AdminBalancePage;
